import React, { useState, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Colors, Spacing, FontSizes, FontWeights, BorderRadius, Shadows } from '../constants/theme';
import UnifiedPageHeader from '../components/UnifiedPageHeader';
import PageContainer from '../components/layout/PageContainer';
import { Candidate } from '../types';
import { mockRecruitmentData } from '../utils/mockRecruitmentData';

const ContentArea = styled.div`
  padding: 0 ${Spacing.xl}px ${Spacing.xl}px;
  height: 100%;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    padding-left: ${Spacing.md}px;
    padding-right: ${Spacing.md}px;
  }
`;

const FiltersAndContent = styled.div`
  display: grid;
  grid-template-columns: 280px 1fr;
  gap: ${Spacing.xl}px;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const FiltersPanel = styled.div`
  background-color: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  padding: ${Spacing.lg}px;
  height: fit-content;
  box-shadow: ${Shadows.sm};
`;

const FilterSection = styled.div`
  margin-bottom: ${Spacing.xl}px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const FilterTitle = styled.h3`
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textSecondary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: ${Spacing.md}px;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${Spacing.sm}px ${Spacing.md}px;
  border: 1px solid ${Colors.border};
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.md}px;
  background-color: ${Colors.background};
  color: ${Colors.textPrimary};
  
  &:focus {
    outline: none;
    border-color: ${Colors.primary};
  }
`;

const FilterCheckbox = styled.label`
  display: flex;
  align-items: center;
  gap: ${Spacing.sm}px;
  padding: ${Spacing.xs}px 0;
  cursor: pointer;
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textPrimary};
  
  input {
    width: 16px;
    height: 16px;
    accent-color: ${Colors.primary};
  }
  
  &:hover {
    color: ${Colors.primary};
  }
`;

const FilterBadge = styled.span`
  background-color: ${Colors.backgroundDark};
  color: ${Colors.textTertiary};
  padding: 2px 6px;
  border-radius: ${BorderRadius.round}px;
  font-size: ${FontSizes.xs}px;
  margin-left: auto;
`;

const MainContent = styled.div`
  background-color: ${Colors.surface};
  border-radius: ${BorderRadius.lg}px;
  box-shadow: ${Shadows.sm};
  overflow: hidden;
`;

const TableHeader = styled.div`
  padding: ${Spacing.lg}px;
  border-bottom: 1px solid ${Colors.borderLight};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ResultsCount = styled.div`
  font-size: ${FontSizes.md}px;
  color: ${Colors.textSecondary};
`;

const BulkActions = styled.div`
  display: flex;
  gap: ${Spacing.md}px;
`;

const ActionButton = styled.button`
  padding: ${Spacing.sm}px ${Spacing.md}px;
  background-color: ${Colors.primary};
  color: ${Colors.textOnPrimary};
  border: none;
  border-radius: ${BorderRadius.sm}px;
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.medium};
  cursor: pointer;
  transition: all 150ms ease;
  
  &:hover {
    background-color: ${Colors.primaryDark};
  }
  
  &:disabled {
    background-color: ${Colors.borderLight};
    cursor: not-allowed;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background-color: ${Colors.backgroundDark};
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${Colors.borderLight};
  transition: all 150ms ease;
  cursor: pointer;
  
  &:hover {
    background-color: ${Colors.surfaceHover};
  }
`;

const TableCell = styled.td`
  padding: ${Spacing.md}px ${Spacing.lg}px;
  font-size: ${FontSizes.sm}px;
  color: ${Colors.textPrimary};
`;

const TableHeaderCell = styled.th`
  padding: ${Spacing.md}px ${Spacing.lg}px;
  font-size: ${FontSizes.sm}px;
  font-weight: ${FontWeights.semibold};
  color: ${Colors.textSecondary};
  text-align: left;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const CandidateName = styled.div`
  font-weight: ${FontWeights.medium};
  color: ${Colors.textPrimary};
  margin-bottom: 2px;
`;

const CandidateEmail = styled.div`
  font-size: ${FontSizes.xs}px;
  color: ${Colors.textTertiary};
`;

const StageBadge = styled.span<{ stage: string }>`
  display: inline-block;
  padding: 4px 12px;
  border-radius: ${BorderRadius.round}px;
  font-size: ${FontSizes.xs}px;
  font-weight: ${FontWeights.semibold};
  background-color: ${props => {
    switch (props.stage) {
      case 'APPLIED': return Colors.info;
      case 'SCREENING': return Colors.warning;
      case 'INTERVIEW': return Colors.primary;
      case 'OFFER': return Colors.success;
      case 'HIRED': return Colors.accent;
      case 'REJECTED': return Colors.danger;
      case 'WITHDRAWN': return Colors.borderDark;
      default: return Colors.borderLight;
    }
  }};
  color: ${Colors.textOnPrimary};
`;

const SkillTag = styled.span`
  display: inline-block;
  padding: 2px 8px;
  background-color: ${Colors.backgroundDark};
  color: ${Colors.textSecondary};
  border-radius: ${BorderRadius.xs}px;
  font-size: ${FontSizes.xs}px;
  margin-right: ${Spacing.xs}px;
`;

const ScoreIndicator = styled.div<{ score: number }>`
  display: flex;
  align-items: center;
  gap: ${Spacing.xs}px;
  
  .score-bar {
    width: 60px;
    height: 6px;
    background-color: ${Colors.backgroundDark};
    border-radius: 3px;
    overflow: hidden;
    
    .score-fill {
      height: 100%;
      background-color: ${props => 
        props.score >= 80 ? Colors.success :
        props.score >= 60 ? Colors.warning :
        Colors.danger
      };
      width: ${props => props.score}%;
    }
  }
  
  .score-text {
    font-size: ${FontSizes.xs}px;
    color: ${Colors.textSecondary};
    min-width: 30px;
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  accent-color: ${Colors.primary};
  cursor: pointer;
`;

const CandidateListScreen: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [candidates] = useState<Candidate[]>(mockRecruitmentData.candidates);
  const [selectedCandidates, setSelectedCandidates] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [stageFilters, setStageFilters] = useState<Set<string>>(new Set());
  const [requisitionFilters, setRequisitionFilters] = useState<Set<string>>(new Set());
  const [sourceFilters, setSourceFilters] = useState<Set<string>>(new Set());

  // Initialize stage filter from URL params
  React.useEffect(() => {
    const stage = searchParams.get('stage');
    if (stage) {
      setStageFilters(new Set([stage.toUpperCase()]));
    }
  }, [searchParams]);

  const filteredCandidates = useMemo(() => {
    return candidates.filter(candidate => {
      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        if (!candidate.name.toLowerCase().includes(query) &&
            !candidate.email.toLowerCase().includes(query) &&
            !candidate.requisitionTitle.toLowerCase().includes(query)) {
          return false;
        }
      }

      // Stage filter
      if (stageFilters.size > 0 && !stageFilters.has(candidate.stage)) {
        return false;
      }

      // Requisition filter
      if (requisitionFilters.size > 0 && !requisitionFilters.has(candidate.requisitionId)) {
        return false;
      }

      // Source filter
      if (sourceFilters.size > 0 && !sourceFilters.has(candidate.source)) {
        return false;
      }

      return true;
    });
  }, [candidates, searchQuery, stageFilters, requisitionFilters, sourceFilters]);

  const handleCandidateClick = (candidateId: string) => {
    navigate(`/candidates/${candidateId}`);
  };

  const handleSelectAll = () => {
    if (selectedCandidates.size === filteredCandidates.length) {
      setSelectedCandidates(new Set());
    } else {
      setSelectedCandidates(new Set(filteredCandidates.map(c => c.id)));
    }
  };

  const handleSelectCandidate = (candidateId: string) => {
    const newSelection = new Set(selectedCandidates);
    if (newSelection.has(candidateId)) {
      newSelection.delete(candidateId);
    } else {
      newSelection.add(candidateId);
    }
    setSelectedCandidates(newSelection);
  };

  const toggleFilter = (filterSet: Set<string>, setFilterSet: Function, value: string) => {
    const newSet = new Set(filterSet);
    if (newSet.has(value)) {
      newSet.delete(value);
    } else {
      newSet.add(value);
    }
    setFilterSet(newSet);
  };

  const stageCounts = {
    APPLIED: candidates.filter(c => c.stage === 'APPLIED').length,
    SCREENING: candidates.filter(c => c.stage === 'SCREENING').length,
    INTERVIEW: candidates.filter(c => c.stage === 'INTERVIEW').length,
    OFFER: candidates.filter(c => c.stage === 'OFFER').length,
    HIRED: candidates.filter(c => c.stage === 'HIRED').length,
    REJECTED: candidates.filter(c => c.stage === 'REJECTED').length,
    WITHDRAWN: candidates.filter(c => c.stage === 'WITHDRAWN').length,
  };

  const uniqueRequisitions = Array.from(new Set(candidates.map(c => c.requisitionTitle)));
  const uniqueSources = Array.from(new Set(candidates.map(c => c.source)));

  return (
    <PageContainer>
      <UnifiedPageHeader
        title="Candidate Pipeline"
        subtitle="Manage and track all candidates across your requisitions"
        metadata={{
          timestamp: new Date(),
          count: 0,
          status: 'Live'
        }}
        actions={[]}
      />
      
      <ContentArea>
        <FiltersAndContent>
        <FiltersPanel>
          <FilterSection>
            <FilterTitle>Search</FilterTitle>
            <SearchInput
              type="text"
              placeholder="Search by name, email, or role..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </FilterSection>

          <FilterSection>
            <FilterTitle>Stage</FilterTitle>
            {Object.entries(stageCounts).map(([stage, count]) => (
              <FilterCheckbox key={stage}>
                <input
                  type="checkbox"
                  checked={stageFilters.has(stage)}
                  onChange={() => toggleFilter(stageFilters, setStageFilters, stage)}
                />
                <span>{stage.charAt(0) + stage.slice(1).toLowerCase()}</span>
                <FilterBadge>{count}</FilterBadge>
              </FilterCheckbox>
            ))}
          </FilterSection>

          <FilterSection>
            <FilterTitle>Requisition</FilterTitle>
            {uniqueRequisitions.map(req => (
              <FilterCheckbox key={req}>
                <input
                  type="checkbox"
                  checked={requisitionFilters.has(req)}
                  onChange={() => toggleFilter(requisitionFilters, setRequisitionFilters, req)}
                />
                <span>{req}</span>
              </FilterCheckbox>
            ))}
          </FilterSection>

          <FilterSection>
            <FilterTitle>Source</FilterTitle>
            {uniqueSources.map(source => (
              <FilterCheckbox key={source}>
                <input
                  type="checkbox"
                  checked={sourceFilters.has(source)}
                  onChange={() => toggleFilter(sourceFilters, setSourceFilters, source)}
                />
                <span>{source}</span>
              </FilterCheckbox>
            ))}
          </FilterSection>
        </FiltersPanel>

        <MainContent>
          <TableHeader>
            <ResultsCount>
              {filteredCandidates.length} candidates
              {selectedCandidates.size > 0 && ` • ${selectedCandidates.size} selected`}
            </ResultsCount>
            <BulkActions>
              <ActionButton disabled={selectedCandidates.size === 0}>
                Move Stage
              </ActionButton>
              <ActionButton disabled={selectedCandidates.size === 0}>
                Assign Recruiter
              </ActionButton>
              <ActionButton disabled={selectedCandidates.size === 0}>
                Export
              </ActionButton>
            </BulkActions>
          </TableHeader>

          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell style={{ width: '40px' }}>
                  <Checkbox
                    type="checkbox"
                    checked={selectedCandidates.size === filteredCandidates.length && filteredCandidates.length > 0}
                    onChange={handleSelectAll}
                  />
                </TableHeaderCell>
                <TableHeaderCell>Candidate</TableHeaderCell>
                <TableHeaderCell>Requisition</TableHeaderCell>
                <TableHeaderCell>Stage</TableHeaderCell>
                <TableHeaderCell>Source</TableHeaderCell>
                <TableHeaderCell>Skills</TableHeaderCell>
                <TableHeaderCell>Score</TableHeaderCell>
                <TableHeaderCell>Applied</TableHeaderCell>
              </TableRow>
            </TableHead>
            <tbody>
              {filteredCandidates.map(candidate => (
                <TableRow key={candidate.id}>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      type="checkbox"
                      checked={selectedCandidates.has(candidate.id)}
                      onChange={() => handleSelectCandidate(candidate.id)}
                    />
                  </TableCell>
                  <TableCell onClick={() => handleCandidateClick(candidate.id)}>
                    <CandidateName>{candidate.name}</CandidateName>
                    <CandidateEmail>{candidate.email}</CandidateEmail>
                  </TableCell>
                  <TableCell onClick={() => handleCandidateClick(candidate.id)}>
                    {candidate.requisitionTitle}
                  </TableCell>
                  <TableCell onClick={() => handleCandidateClick(candidate.id)}>
                    <StageBadge stage={candidate.stage}>
                      {candidate.stage}
                    </StageBadge>
                  </TableCell>
                  <TableCell onClick={() => handleCandidateClick(candidate.id)}>
                    {candidate.source}
                  </TableCell>
                  <TableCell onClick={() => handleCandidateClick(candidate.id)}>
                    {candidate.skills.slice(0, 3).map(skill => (
                      <SkillTag key={skill}>{skill}</SkillTag>
                    ))}
                    {candidate.skills.length > 3 && (
                      <SkillTag>+{candidate.skills.length - 3}</SkillTag>
                    )}
                  </TableCell>
                  <TableCell onClick={() => handleCandidateClick(candidate.id)}>
                    {candidate.assessmentScore && (
                      <ScoreIndicator score={candidate.assessmentScore}>
                        <div className="score-bar">
                          <div className="score-fill" />
                        </div>
                        <span className="score-text">{candidate.assessmentScore}%</span>
                      </ScoreIndicator>
                    )}
                  </TableCell>
                  <TableCell onClick={() => handleCandidateClick(candidate.id)}>
                    {new Date(candidate.appliedDate).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))}
            </tbody>
          </Table>
        </MainContent>
        </FiltersAndContent>
      </ContentArea>
    </PageContainer>
  );
};

export default CandidateListScreen;